import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the shape of our global data
interface DocumentContextType {
  file: File | null;
  analysisData: any | null;

  // --- NEW: Temporary Backpack for "Split & Redact" Workflow ---
  tempRedactionPayload: any | null;
  setTempRedactionPayload: (data: any) => void;

  // Processing mode: tracks which workflow the user selected
  processMode: 'redact' | 'split' | 'both' | 'promissory_note' | null;
  setProcessMode: (mode: 'redact' | 'split' | 'both' | 'promissory_note' | null) => void;

  // Signing dates from MIke scan — used by promissory_note flow to
  // subdivide SSN segments at form-level boundaries.
  signingDates: any[];
  setSigningDates: (dates: any[]) => void;

  // Helpers
  setDocumentContext: (file: File, data: any) => void;
  clearDocumentContext: () => void;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

export function DocumentProvider({ children }: { children: ReactNode }) {
  const [file, setFile] = useState<File | null>(null);
  const [analysisData, setAnalysisData] = useState<any | null>(null);

  // State to hold redactions while moving to the Splitter page
  const [tempRedactionPayload, setTempRedactionPayload] = useState<any | null>(null);

  // Processing mode selected by user (redact, split, both, promissory_note)
  const [processMode, setProcessMode] = useState<'redact' | 'split' | 'both' | 'promissory_note' | null>(null);

  // Signing dates from MIke scan (per-form, used for promissory_note splitting)
  const [signingDates, setSigningDates] = useState<any[]>([]);

  const setDocumentContext = (newFile: File, newData: any) => {
    setFile(newFile);
    setAnalysisData(newData);
    // Important: Reset the backpack when a new file is loaded
    setTempRedactionPayload(null);
    setProcessMode(null);
    setSigningDates([]);
  };

  const clearDocumentContext = () => {
    setFile(null);
    setAnalysisData(null);
    setTempRedactionPayload(null);
    setProcessMode(null);
    setSigningDates([]);
  };

  return (
    <DocumentContext.Provider value={{
      file,
      analysisData,
      tempRedactionPayload,
      setTempRedactionPayload,
      processMode,
      setProcessMode,
      signingDates,
      setSigningDates,
      setDocumentContext,
      clearDocumentContext
    }}>
      {children}
    </DocumentContext.Provider>
  );
}

// Custom hook for easy access
export function useDocumentContext() {
  const context = useContext(DocumentContext);
  if (context === undefined) {
    throw new Error('useDocumentContext must be used within a DocumentProvider');
  }
  return context;
}